import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  AwardSubCriterionFields,
  SUB_CRITERION_HOST_TYPES,
  type AwardSubCriterionFormValues,
  type AwardSubCriterionSubmitValues,
} from "../../../common/library/fields/AwardSubCriterionFields";
import { RequirementDialogHeader } from "../../../common/components/RequirementDialogHeader";

// t('New sub-criterion') t('Edit sub-criterion') t('Cancel') t('Save')

export type { AwardSubCriterionFormValues };

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  mode: "add" | "edit";
  defaultValues?: AwardSubCriterionFormValues;
  /**
   * Set when the requirement is written inside a criterion, which then answers both open questions: the
   * "belongs under" field goes away, and the weight appears — a share of that criterion, which a
   * requirement kept on its own has no way to have.
   */
  inheritsCriterionType?: boolean;
  onSubmit: (values: AwardSubCriterionSubmitValues) => Promise<void>;
  onClose: () => void;
}

const FORM_ID = "award-sub-criterion-modal-form";

/**
 * A requirement in the library — standalone, where it has to say which kind of criterion it belongs
 * under, or written inside one, where the criterion is the answer.
 */
export const AwardSubCriterionModal = ({
  isOpen,
  isSaving,
  mode,
  defaultValues,
  inheritsCriterionType = false,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <RequirementDialogHeader title={t(mode === "edit" ? "Edit sub-criterion" : "New sub-criterion")} />
        <ModalBody>
          <AwardSubCriterionFields
            formId={FORM_ID}
            defaultValues={defaultValues}
            criterionTypes={inheritsCriterionType ? undefined : SUB_CRITERION_HOST_TYPES}
            // Inside a criterion the requirement has siblings, and their shares total 100 within it and
            // travel with the criterion — so the weight is asked for, but not insisted on: a definition
            // may be incomplete, and one picked from the library arrives without a weight at all. Kept
            // on its own there is nothing to be a share of, and no field for one either.
            weight={inheritsCriterionType ? "optional" : "hidden"}
            onSubmit={onSubmit}
          />
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="smBorder">
          <Flex gap="3">
            <Button variant="outline" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button type="submit" form={FORM_ID} colorScheme="teal" isLoading={isSaving}>
              {t("Save")}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
